import {auth} from './firebaseApp.ts';

import './App.css'
import {BrowserRouter, Route, Routes} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {AuthState, isAuthenticatedUser} from "./api/accessControl/AuthState.ts";
import {User} from "./api/accessControl/User.ts";
import LoadingFrame from "./components/LoadingFrame.tsx";
import Intercom from '@intercom/messenger-js-sdk';
import {HotJar} from "./lib/hotjar.ts";
import {resolveRoutesForAuthState} from "./Routes.tsx";
import * as Sentry from "@sentry/react";
import {processCurrentAuthState} from "./lib/TFICAuth.ts";
import {BackendApi} from "./api/api.ts";
import {NotificationProvider} from "./lib/NotificationContext.tsx";
import {FilterStoreProvider} from "./contexts/FilterStore.tsx";
import {QUINN_DOCUMENT_DRAWER_LAYER, QuinnPanel, QuinnProvider} from "./views/Quinn";
import {DocumentDrawerProvider} from "./views/DocumentDrawer";
import {canUseQuinn} from "./lib/quinnAccess.ts";
import {UploadQueuePanel, UploadQueueProvider} from "./views/UploadQueue";


function addSubdomainPreservingAll(
    subdomain: string = 'blog',
    baseUrl: string | URL = window.location.href
): string {
    try {
        const url = new URL(baseUrl);

        url.hostname = `${subdomain}.${url.hostname}`;

        return url.toString();

    } catch (error) {
        console.error("Error processing URL:", error);
        const baseUrlString = typeof baseUrl === 'string' ? baseUrl : baseUrl.href;
        throw new TypeError(`Invalid base URL provided: ${baseUrlString}`);
    }
}

async function onSuccessfulAuthenticationCallback(user: User, api: BackendApi) {
    Intercom({
        app_id: 'ah53zlwi',
        user_id: user.id,
        name: user.getFullName(),
        email: user.email,
        vertical_padding: 56,
    });

    HotJar.tagUserSession(user)

    Sentry.setUser({
        user_id: user.id,
        name: user.getFullName(),
        email: user.email,
    });

    if (user && user.partnerId && !globalThis.isWhiteLabeled) {
        const partner = await api.Partner__findByBusinessId(user.partnerId)
        window.location.replace(addSubdomainPreservingAll(partner.result.slug))
    }
}


function App() {
    const [routes, setRoutes] = useState([]);
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    useEffect(() => {
        auth.onAuthStateChanged(async (firebaseUser: any) => {
            try {
                const currentAuthState: AuthState = await processCurrentAuthState(
                    firebaseUser,
                    onSuccessfulAuthenticationCallback,
                );
                if (!isAuthenticatedUser(currentAuthState)) {
                    Intercom({
                        app_id: 'ah53zlwi',
                        vertical_padding: 56,
                    });
                    setCurrentUser(null);
                } else {
                    setCurrentUser(currentAuthState);
                }

                const routes = await resolveRoutesForAuthState(currentAuthState);
                setRoutes(routes);
            } catch (error) {
                setCurrentUser(null);
                const routes = await resolveRoutesForAuthState(null);
                setRoutes(routes);
            }
        });
    }, [])

    if (!routes.length)
        return (
            <NotificationProvider>
                <FilterStoreProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route path='*' element={<LoadingFrame isLoading={true}><span>Loading...</span></LoadingFrame>}/>
                        </Routes>
                    </BrowserRouter>
                </FilterStoreProvider>
            </NotificationProvider>
        )

    const quinnEnabled = canUseQuinn(currentUser);

    const appRoutes = (
        <Routes>
            {routes && routes.map(routeElement => (routeElement))}
        </Routes>
    );

    const appContent = quinnEnabled ? (
        <QuinnProvider
            key={currentUser!.id}
            userId={currentUser!.id}
            canAskAllClients={currentUser!.flags.getFlag('PARTNER__QUINN_DB_TOOLS')}
        >
            {appRoutes}
            <DocumentDrawerProvider currentUser={currentUser!} layerClassName={QUINN_DOCUMENT_DRAWER_LAYER}>
                <QuinnPanel />
            </DocumentDrawerProvider>
        </QuinnProvider>
    ) : (
        appRoutes
    );

    return (
        <NotificationProvider>
            <FilterStoreProvider>
                <BrowserRouter>
                    {currentUser ? (
                        <UploadQueueProvider key={currentUser.id}>
                            {appContent}
                            <UploadQueuePanel />
                        </UploadQueueProvider>
                    ) : (
                        appContent
                    )}
                </BrowserRouter>
            </FilterStoreProvider>
        </NotificationProvider>
    )


}

export default App
